//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import React, { useEffect } from 'react';
import styled from 'styled-components';

import {
  FlatDefaultButton,
  FlatPrimaryButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import CalendarView from '@components/date-range-picker/CalendarView';
import CustomRangeDisplay from '@components/date-range-picker/CustomRangeDisplay';
import { DateRangePickerContextProvider } from '@components/date-range-picker/DateRangePickerContext';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { useDateRangeSelectionModel } from '@components/date-range-picker/useDateRangeSelectionModel';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { isDefined } from '@utils/filterPredicates';

interface DateRangePickerPanelProps {
  aliases?: IAliasedDateRange[];
  className?: string;
  footnote?: React.ReactNode;
  initialValue: IAliasedDateRange;
  isOpen: boolean;
  maxDate?: DateString;
  maxDateRange?: number;
  minDate?: DateString;
  onCancelClicked?: () => void;
  onSetDateClicked?: (range: IAliasedDateRange) => void;
  timeZone: TimeZoneId;
}

interface IAliasButtonProps
  extends React.ComponentProps<
    // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
    typeof FlatPrimaryButton | typeof FlatDefaultButton
  > {
  isActiveAlias: boolean;
}
/**
 * Use different styles of button depending on whether the alias is active or not.
 */
const AliasButton = (props: IAliasButtonProps) => {
  const { isActiveAlias, ...restProps } = props;
  return isActiveAlias ? (
    <FlatPrimaryButton {...restProps} />
  ) : (
    <FlatDefaultButton {...restProps} />
  );
};

const DateRangePickerPanel = ({
  aliases = [],
  footnote,
  initialValue,
  isOpen,
  maxDate,
  maxDateRange,
  minDate,
  onCancelClicked = () => undefined,
  onSetDateClicked = () => undefined,
  timeZone,
}: DateRangePickerPanelProps) => {
  const selection = useDateRangeSelectionModel(
    initialValue.startDate,
    initialValue.endDate,
    initialValue.displayName,
    minDate,
    maxDate,
    maxDateRange,
  );

  const hasFullRangeSelected =
    isDefined(selection.startDate) && isDefined(selection.endDate);

  const isCustomRange = ((a) => !a || a === 'Custom')(selection.aliasKey);
  const hasInvalidCustomRange =
    isCustomRange &&
    !(selection.customEndDate.isValid && selection.customStartDate.isValid);

  // If endDate, set left month to be 1 month before end date
  const initialDate = isDefined(selection.endDate)
    ? DateString.addMonths(selection.endDate, -1)
    : selection.startDate ?? DateString.getToday(timeZone);

  const handleCancel = () => {
    onCancelClicked();
  };

  useEffect(() => {
    if (!isOpen) {
      selection.setRange(initialValue);
    }
  }, [isOpen]);

  return (
    <DateRangePickerContextProvider
      value={{ minDate, maxDate, maxDateRange, ...selection }}
    >
      <Panel>
        <Body>
          <CalendarContainer>
            <CalendarView
              initialDate={DateString.applyMomentSetter(initialDate, 'date', 1)}
            ></CalendarView>
          </CalendarContainer>
          <AliasContainer>
            {aliases.map((alias) => (
              <AliasButton
                isActiveAlias={selection.aliasKey === alias.displayName}
                key={alias.displayName}
                onClick={() => selection.setRange(alias)}
              >
                <AliasButtonText>{alias.displayName}</AliasButtonText>
              </AliasButton>
            ))}
            <AliasButton
              isActiveAlias={isCustomRange}
              key='Custom'
              onClick={() => selection.swapToCustomRange()}
            >
              <AliasButtonText>Custom</AliasButtonText>
            </AliasButton>
            {isCustomRange && (
              <RangeDisplayContainer>
                <RangeInputs />
              </RangeDisplayContainer>
            )}
          </AliasContainer>
        </Body>
        <Footer>
          <div>
            {
              // If footnote requires date picker panel state information, we could:
              // A. Use a render props pattern
              // OR
              // B. Use the connect/monitor pattern to move ownership of the state system to the consumer.
              footnote
            }
          </div>
          <div>
            <StyledFlatPrimaryButton onClick={handleCancel}>
              Cancel
            </StyledFlatPrimaryButton>
            <PrimaryButton
              isDisabled={!hasFullRangeSelected || hasInvalidCustomRange}
              onClick={() =>
                selection.startDate &&
                selection.endDate &&
                onSetDateClicked({
                  displayName: selection.aliasKey ?? 'Custom',
                  startDate: selection.startDate,
                  endDate: selection.endDate,
                  timeZone,
                })
              }
            >
              Set Date
            </PrimaryButton>
          </div>
        </Footer>
      </Panel>
    </DateRangePickerContextProvider>
  );
};

const Panel = styled.div(
  ({ theme }) => `
    display: inline-block;
`,
);

const Body = styled.div(
  ({ theme }) => `
    display: inline-flex;
`,
);

const Footer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacings[4]};
    border-top: 1px solid ${theme.colors.gray2};
`,
);
const AliasContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    padding: ${theme.spacings[3]};
    padding-right: ${theme.spacings[4]};
    border-left: 1px solid ${theme.colors.gray2};
`,
);

const AliasButtonText = styled.div(
  ({ theme }) => `
    // Same arbitrary width from the old date range picker
    width: 160px;
    text-align: left;
`,
);

const RangeDisplayContainer = styled.div(
  ({ theme }) => `
  padding-left: ${theme.spacings[3]};
  padding-top: ${theme.spacings[2]};
`,
);
const RangeInputs = styled(CustomRangeDisplay)(
  ({ theme }) => `
  // Same arbitrary width from the old date range picker
  width: 176px;
`,
);

const CalendarContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[4]};
`,
);

const StyledFlatPrimaryButton = styled(FlatPrimaryButton)(
  ({ theme }) => `
  margin-right: ${theme.spacings[1]};
`,
);

export default DateRangePickerPanel;
